// istanbul ignore file

import {useEffect, useMemo, useState} from 'react'
import {useLocation} from 'react-router-dom'
import useOcapiSite from '../../commerce-api/hooks/useOcapiSite'
import useMultiSite from '../../hooks/use-multi-site'
import {DATA_LAYER_INITIALIZATION_EVENT, trackEvent} from '../analytics'
import useBasket from '../../commerce-api/hooks/useBasket'
import usePageVisibility from '../../hooks/use-page-visibility'
import {INTERVAL_TO_EMIT_VIRTUAL_PAGE_EVENT} from '../../constants'
import useEffectEvent from '../../hooks/use-effect-event'
import useCustomer from '../../commerce-api/hooks/useCustomer'

export const useInitialDataLayer = ({pageType}) => {
    const {allowedLocales} = useOcapiSite()
    const {pathname} = useLocation()
    const {locale, site} = useMultiSite()
    const basket = useBasket()
    const isPageVisible = usePageVisibility()
    // track page visibility and when it changed
    const [pageVisibilityState, setPageVisibilityState] = useState({
        timestamp: Date.now(),
        visible: isPageVisible
    })

    const customer = useCustomer()

    const [customerInfo, setCustomerInfo] = useState()

    useEffect(() => {
        if (!customer.isInitialized) {
            return
        }

        if (customer.isRegistered) {
            setCustomerInfo({
                user_type: 'customer',
                user_loggedIn: true,
                user_total_orders: customer.c_ordersTotal
            })
        } else {
            setCustomerInfo({
                user_type: 'guest',
                user_loggedIn: false
            })
        }
    }, [customer])

    // force to trigger emitting the event
    const [triggerEvent, setTriggerEvent] = useState(0)

    // track page visibility and trigger the event if the page become visible after at least 30 minutes
    useEffect(() => {
        // if visibility didn't change to nothing
        if (pageVisibilityState.visible === isPageVisible) {
            return
        }

        // if page become visible and the last time it was visible was more than 30 minutes ago
        if (
            isPageVisible &&
            Date.now() - pageVisibilityState.timestamp >= INTERVAL_TO_EMIT_VIRTUAL_PAGE_EVENT
        ) {
            // trigger the event
            setTriggerEvent((i) => i + 1)
        }

        // update the state
        setPageVisibilityState({timestamp: Date.now(), visible: isPageVisible})
    }, [isPageVisible, pageVisibilityState])

    const localeInfo = useMemo(() => {
        return allowedLocales?.find((info) => info.id === (locale.id || locale))
    }, [locale, allowedLocales])

    // use useEffectEvent to create a function to append latest values to the event without triggering a new event
    const onTrackEvent = useEffectEvent((event) =>
        trackEvent({
            ...event,
            // include all properties, that should not trigger the tracking, but are needed as part of the event
            cart_value: basket?.orderTotal,
            has_cookiebot: site?.gdpr?.enableCookiebot || false,
            has_ecommerce: !site?.isContentOnlySite
        })
    )
    // trigger the event when page changes or forced with triggerEvent
    useEffect(() => {
        // do not track if there is no locale info or the currency still undefined
        if (!localeInfo || !basket?.currency) {
            return
        }
        const email = customer?.email || basket?.customerInfo?.email

        onTrackEvent({
            event: DATA_LAYER_INITIALIZATION_EVENT,
            page_type: pageType,
            page_language: localeInfo.language,
            page_country2: localeInfo.country,
            page_country3: localeInfo.iso3Country,
            canonical_url_path: pathname,
            page_currency: basket?.currency,
            ecommerce: {
                currency: basket?.currency
            },
            ...(email && {email: email}),
            ...customerInfo
        })
    }, [
        pageType,
        pathname,
        localeInfo,
        basket?.currency,
        basket?.customerInfo?.email,
        customer.email,
        customerInfo,
        triggerEvent,
        onTrackEvent
    ])
}
